import { useCallback, useRef, useState } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import useAuth from '../auth/useAuth';
import AuthLayout from './components/AuthLayout';
import AuthTitle from './components/AuthTitle';
import './LoginPage.css';

const signInSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
  ...(process.env.REACT_APP_DISABLE_CAPTCHA !== 'true' && {
    recaptcha: Yup.string().required('ReCaptcha is required'),
  }),
});

const LoginPage = () => {
  const auth = useAuth();
  const history = useHistory();
  const reCaptchaRef = useRef();
  const [erCaptcha, setErCaptcha] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      recaptcha: '',
    },
    validationSchema: signInSchema,
    onSubmit: (values) => {
      handleSubmit(values.email, values.password);
    },
  });

  const handleCaptchaChange = useCallback((value) => {
    formik.setFieldValue('recaptcha', value);
    setErCaptcha(!value);
  }, []);

  const handleSubmit = async (email, password) => {
    if (!formik.values.recaptcha && process.env.REACT_APP_DISABLE_CAPTCHA !== 'true') {
      setErCaptcha(true);
      return;
    }
    setErCaptcha(false);
    try {
      await auth.login(email, password);
    } catch (error) {
      formik.setErrors({ submit: 'Unrecognised Details' });
    }
  };

  if (auth.isAuthenticated) return <Redirect to="/admin/overview" />;

  return (
    <AuthLayout>
      <div className="login-page__container">
        <AuthTitle>Log In</AuthTitle>
        <p className="login-page__subtitle">
          Don't have an account?{' '}
          <a href="/signup" onClick={(e) => { e.preventDefault(); history.push('/signup'); }}>
            Sign Up
          </a>
        </p>
        <form onSubmit={formik.handleSubmit} className="login-page__form">
          <input
            type="email"
            placeholder="Email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`login-page__input ${formik.touched.email && formik.errors.email ? 'login-page__input--error' : ''}`}
          />
          {formik.touched.email && formik.errors.email && (
            <span className="login-page__error">{formik.errors.email}</span>
          )}
          <input
            type="password"
            placeholder="Password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            className={`login-page__input ${formik.touched.password && formik.errors.password ? 'login-page__input--error' : ''}`}
          />
          {formik.touched.password && formik.errors.password && (
            <span className="login-page__error">{formik.errors.password}</span>
          )}
          <a 
            href="/forgot"
            className="login-page__forgot"
            onClick={(e) => { e.preventDefault(); history.push('/forgot'); }}
          >
            Forgot password?
          </a>
          {formik.errors.submit && (
            <div className="login-page__alert">{formik.errors.submit}</div>
          )}
          <div className="login-page__action-row">
            <div className="login-page__captcha">
              {process.env.REACT_APP_DISABLE_CAPTCHA !== 'true' && (
                <ReCAPTCHA
                  ref={reCaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6Lc6O9AUAAAAAF6myYURrvZwW43E4ShPeKOVKCHe"}
                  onChange={handleCaptchaChange}
                />
              )}
              {(formik.touched.recaptcha && formik.errors.recaptcha) || erCaptcha ? (
                <span className="login-page__error">
                  {formik.errors.recaptcha || 'ReCaptcha error'}
                </span>
              ) : null}
            </div>
            <button type="submit" className="login-page__button">
              Sign In
            </button>
          </div>
        </form>
      </div>
    </AuthLayout>
  );
};

export default LoginPage;
